import { Fragment, useEffect, useState, useMemo } from 'react';

//components
import { Box } from '@material-ui/core';
import UserSection from './UserSection';
import DelegationSection from './DelegationSection';
import Footer from './Footer';
import DeleteDelegationModal from './DeleteDelegationModal';
import Drawer from 'components/common/Drawer';

//selectors
import { manageDoALogSelector } from 'common/permissions/selectors';
import { dataSourceDataTypeSelector, dataSourceSelector } from 'pages/dataSource/selectors';
import {
    delegationsList,
    getDelegationAvailableUserOptions,
    getDelegationTaskOptions,
    getDelegationRoleOptions,
} from 'pages/delegationOfAuthority/v2/selectors';

//styles
import { makeStyles } from '@material-ui/core/styles';

//utils
import { connect } from 'react-redux';
import lodash, { some, get, cloneDeep, map, isEmpty } from 'lodash';
import { dataSourceLogActions, dataSourceStatus } from 'pages/dataSource/utils/constants';
import { getRawValue, getStringValue } from 'common/util/dataTypeValues';
import { verifyNoPITenuresOverlapped } from 'pages/delegationOfAuthority/v2/util/validators';
import {
    isPITenureOverlappingWithDelegation,
    isActivePI,
    shouldUserInDropDownSignature,
    isDelegationTheSameWithDefault
} from 'pages/delegationOfAuthority/util/predicates';
import { manageStudyTeamMemberPredicate, manageAdminsPredicate, manageStudyDelegationPredicate } from 'common/permissions/studyPredicates';
import { loadHomePageV4Access } from 'common/features/predicates';
import moment from 'moment';
import uuid from 'node-uuid';

//actions
import {
    deleteStudyDelegationV2,
    createStudyDelegationForStudy,
    updateStudyDelegationV2
} from 'pages/delegationOfAuthority/v2/actions/studyDelegationsV2';

import {
    retrieveStudyAuditLogData,
} from 'pages/delegationOfAuthority/v2/actions/studyAuditLogV2';
import {
    getStudyDescriptors,
    getStudyRoles,
    createTeamMember,
    editStudyTeamMember,
    removeStudyTeamMember,
    getStudyTeam
} from 'pages/studyOverview/actions/studySettings';

// import { loadStudyTeam } from 'pages/delegationOfAuthority/v2/actions/studyTeam';

import {
    loadDataSource,
    unloadDataSource,
    saveDataSource,
    writeDataSourceValues,
    loadDataSourceLog,
} from 'pages/dataSource/actions/dataSource';

const PI_ROLE = 'Principal Investigator';
const PI_LABEL = 'P.I.';
const STAFF_LABEL = 'Staff';

const useStyles = makeStyles({
    root: {
        width: '600px',
        height: '100%',
        padding: '20px',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        fontFamily: 'Hind'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        paddingBottom: '20px',
        marginBottom: '20px',
        borderBottom: '1px solid #B1B1B1',
        '& > p': {
            color: '#2057AD',
            fontWeight: 600,
        },
        '& > svg': {
            color: '#151B94',
            cursor: 'pointer',
            fontSize: '20px',
            fontWeight: 900,
            '&:hover': {
                color: 'red'
            }
        },
    },
    userSection: {
        paddingBottom: '20px',
        marginBottom: '20px',
        borderBottom: '1px solid #B1B1B1',
    },
    footer: {
        margin: '20px 20px 0',
        padding: '10px 0',
        width: '600px',
        borderTop: '1px solid #B1B1B1',
        position: 'fixed',
        bottom: '0px',
        backgroundColor: 'inherit'
    }
});

function DelegationDrawer(props) {
    const { open, data = [], studyTeam, onCancel, studySettings, roleOptions, taskOptions, dataType, dataSource, complion4FlagEnabled } = props;

    const classes = useStyles();
    const [isEditing, setIsEditing] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [shouldRemoveUserFromStudy, setShouldRemoveUserFromStudy] = useState(false);
    const [isCompleteSignatures, setIsCompleteSignatures] = useState(false);
    const [suggestedSignatures, setSuggestedSignatures] = useState([]);
    const [selectedSignatures, setSelectedSignatures] = useState(null);
    const [isUserNotStudyMember, setIsUserNotStudyMember] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedRole, setSelectedRole] = useState(null);
    const [selectedTask, setSelectedTask] = useState(null);
    const [selectedStudyAccess, setSelectedStudyAccess] = useState({ label: '', value: '' });
    const [startDateSelected, setStartDateSelected] = useState(null);
    const [endDateSelected, setEndDateSelected] = useState(null);
    const [selectedDataSource, setSelectedDataSource] = useState(null);
    const [delegationRequiringNewPiSignature, setDelegationRequiringNewPiSignature] = useState(null);
    const [currentStartDate, setCurrentStartDateChanged] = useState(undefined);
    const [currentEndDate, setCurrentEndDateChange] = useState(undefined);
    const [isStudyDashboard, setIsStudyDashboard] = useState(false);
    const [auditNotes, setAuditNotes] = useState('');
    const [userHasDataSource, setUserHasDataSource] = useState(true);
    const [isDirtyData, setIsDirtyData] = useState(false);
    const [hasChangeEndDate, setChangeEndDate] = useState(false);
    const [hasChangeStartDate, setChangeStartDate] = useState(false);
    const [isRequesting, setIsRequesting] = useState(false);
    const [adminCount, setAdminCount] = useState(0);
    const [errors, setErrors] = useState({
        user: false,
        dataSource: false,
        studyAccess: false,
        tenureOverlaps: false,
        role: false,
        duplicatedDataSource: false
    });
    const [isAuditNotesError, setIsAuditNotesError] = useState(false);
    const [headerLabel, setHeaderLabel] = useState('');

    function clearForm() {
        setIsDeleting(false);
        setIsEditing(false);
        setSelectedUser(null);
        setIsUserNotStudyMember(false);
        setSelectedRole(null);
        setSelectedStudyAccess({ label: '', value: '' });
        setSelectedDataSource(null);
        setSelectedSignatures([]);
        setStartDateSelected(null);
        setEndDateSelected(null);
        setCurrentEndDateChange(null);
        setCurrentStartDateChanged(null);
        setErrors({
            user: false,
            dataSource: false,
            studyAccess: false,
            tenureOverlaps: false,
            role: false,
            duplicatedDataSource: false
        });
        setChangeEndDate(false);
        setChangeStartDate(false);
    }

    const hasRequiredFields = () => !(
        lodash.isNil(selectedRole) ||
        lodash.isEmpty(selectedTask) ||
        lodash.isNil(startDateSelected) ||
        (!isEditing && lodash.isEmpty(selectedUser))
    );

    useEffect(() => {
        const {
            retrieveStudyAuditLogData,
            getStudyRoles,
            institutionId
        } = props;

        const studyId = lodash.get(props, ['study', 'id']);
        const binderId = props.currentBinder.id;
        const allTeamMembers = lodash.get(studySettings, 'allStudyTeam', []);
        const count = countAdminUsers(allTeamMembers);


        if (!studyId && !binderId && !institutionId && !open) return;
        setAdminCount(count);

        data.startDate && setStartDateSelected(parseDateFormat(data.startDate));
        if (data.endDate) {
            setEndDateSelected(parseDateFormat(data.endDate));
        }
        const defaultRole = props.roleOptions.find((item) => item.value === data.delegationRole);
        data.delegationRole && setSelectedRole(defaultRole);

        const defaultTasks = lodash.intersectionWith(
            props.taskOptions,
            data.delegatedTasks,
            (option, data) => option.value === data.taskId
        );
        setSelectedTask(defaultTasks);

        if(studyId) {
            retrieveStudyAuditLogData(binderId, studyId, institutionId);
            getStudyRoles(institutionId);
            props.getStudyTeam(studyId, institutionId);
        }
    }, [open]);

    useEffect(() => {
        const { studyTypeId } = props.study;
        const { institutionId, getStudyDescriptors } = props;
        if (!studyTypeId || !institutionId) return;
        getStudyDescriptors(institutionId, studyTypeId);

    }, []);

    useEffect(() => {
        const hasRequiredFields = !(
            lodash.isNil(selectedRole) ||
            lodash.isEmpty(selectedTask) ||
            lodash.isNil(startDateSelected)
        );
        if (!isDirtyData && data && hasRequiredFields) {
            setDataPendingSignature();
        }
    }, [selectedRole, startDateSelected, endDateSelected]);

    useEffect(() => {
        if (selectedSignatures && data && !lodash.isEmpty(data)) {
            const signaturesSelectedIds = lodash.sortBy(
                selectedSignatures.map((item) => item.value)
            );
            const defaultSignatureIds = lodash.sortBy(
                data.pendingSignatures.map((item) => item.signBy)
            );

            if (lodash.isEqual(signaturesSelectedIds, defaultSignatureIds) && isTheSameOldData()) {
                setIsDirtyData(false);
            }
        }
    }, [selectedSignatures]);

    useEffect(() => {
        return () => clearForm();
    }, [open]);


    useEffect(() => {
        if (isAuditNotesError && auditNotes.length > 0) {
            setIsAuditNotesError(false);
        }
    }, [auditNotes, isAuditNotesError]);
    const dataSourceValues = useMemo(() => {
        if (dataSource && dataSource.parameters) {
            return dataSource.parameters.values.filter(value => value.status === 'active');
        }
        return [];
    }, [dataSource]);

    useEffect(() => {
        const studyId = lodash.get(props, ['study', 'id']);
        setIsStudyDashboard(!!studyId);
        return () => {
            setIsStudyDashboard(false);
        };
    }, []);

    useEffect(() => {
        if (errors.user && !!selectedUser) {
            setErrors({
                ...errors,
                user: false
            });
        }
        if (errors.dataSource && !!selectedDataSource) {
            setErrors({
                ...errors,
                dataSource: false
            });
        }
        if (errors.studyAccess && !!selectedStudyAccess.value) {
            setErrors({
                ...errors,
                studyAccess: false
            });
        }
        if (errors.duplicatedDataSource && !!selectedDataSource) {
            setErrors({
                ...errors,
                duplicatedDataSource: false
            });
        }
        if (errors.tenureOverlaps && (hasChangeEndDate || hasChangeStartDate)) {
            setErrors({
                ...errors,
                tenureOverlaps: false
            });
        }
        if (errors.role && !!selectedRole) {
            setErrors({
                ...errors,
                role: false
            });
        }
    }, [selectedDataSource, selectedUser, selectedStudyAccess, startDateSelected, endDateSelected, selectedRole]);

    useEffect(() => {
        if (
            selectedUser &&
            Object.prototype.hasOwnProperty.call(selectedUser, 'nameOfResearcher') &&
            lodash.isEmpty(selectedUser.nameOfResearcher) &&
            !isUserNotStudyMember
        ) {
            setUserHasDataSource(false);
        } else {
            setUserHasDataSource(true);
        }
    }, [selectedUser, isUserNotStudyMember]);

    useEffect(() => {
        if (selectedUser) {
            getDataSourceMatches();
        }
    }, [selectedUser]);

    useEffect(() => {
        if (!data || lodash.isEmpty(data)) {
            setIsEditing(false);
        } else {
            setIsEditing(true);
        }

        if (!open) {
            setIsEditing(false);
            setIsDeleting(false);
        }

        if (data && data.completeSignatures && data.completeSignatures.length > 0) {
            setIsCompleteSignatures(true);
        } else {
            setIsCompleteSignatures(false);
        }
    }, [data, open]);

    useEffect(() => {
        if (props.dataSourceId)
            props.loadDataSource(props.dataSourceId, props.institutionId);
    }, [props.dataSourceId]);

    useEffect(() => {
        if (isDirtyData) {
            hasRequiredFields() ? suggestSignatures() : clearSuggestedSignatures();

            const existingDoAEnabled =
                lodash.get(selectedRole, 'label') === PI_ROLE && data.delegationRole !== PI_ROLE;

            existingDoAEnabled
                ? collectExistingDelegations()
                : setDelegationRequiringNewPiSignature([]);
        }

        if (isTheSameOldData()) {
            setIsDirtyData(false);
            setAuditNotes('');
            setDataPendingSignature();
        }

        if (!endDateSelected) {
            setChangeEndDate(false);
        }

    }, [selectedRole, selectedTask, startDateSelected, endDateSelected, selectedUser]);

    const isTheSameOldData = () => {
        if (lodash.isEmpty(data) && lodash.isNil(selectedTask)) return null;

        const newDelegation = {
            ...lodash.cloneDeep(data),
            ...{
                delegatedTasks: lodash.sortBy(lodash.map(selectedTask, (item) => item.task.id)),
                delegationRole: lodash.get(selectedRole, 'label', ''),
                startDate: moment(startDateSelected).format('MM/DD/YYYY'),
                endDate: moment(endDateSelected).format('MM/DD/YYYY'),
            },
        };

        const defaultDelegation = {
            ...lodash.cloneDeep(data),
            delegatedTasks: lodash.sortBy(lodash.map(data.delegatedTasks, (item) => item.taskId)),
            startDate: moment(data.rawStartDate).format('MM/DD/YYYY'),
            endDate: moment(data.rawEndDate).format('MM/DD/YYYY'),
        };

        return isDelegationTheSameWithDefault(defaultDelegation, newDelegation);
    };

    /**
     * From CRI-1579, attempt to replicate what happens when the role is changed while editing a delegation.  The
     * old code didn't update the eregulatory database.  The "delegation page" code is essentially a copy of edits
     * on the Team Members page - including doing extra updates in the `onClose` function - so I've tried to replicate
     * that same, exact pattern here for the next person.
     *
     * @returns
     */
    const doCloseAndCleanup = async () => {
        const userId = data.userId;
        const studyRoleId = selectedRole.role.id;
        const displayName = data.delegationName;
        const studyRoleName = selectedRole.role.name;

        const [studyUserObjs] = studyTeam.filter(member => member.user.id === userId);
        const studyUserId = !lodash.isEmpty(studyUserObjs) ? studyUserObjs.id : null;
        const studyUserType = !lodash.isEmpty(studyUserObjs) ? studyUserObjs.studyUserType : null;
        const nameOfResearcher = !lodash.isEmpty(studyUserObjs) ? studyUserObjs.nameOfResearcher : null;
        const hasAdminRole = !lodash.isEmpty(studyUserObjs) ? studyUserObjs.hasAdminRole : false;

        if (studyUserId) {
            props.onEdit(
                {
                    id: studyUserId,
                    userId: userId,
                    studyRoleId: studyRoleId,
                    hasAdminRole: hasAdminRole,
                    nameOfResearcher: nameOfResearcher,
                    displayName: displayName,
                    studyRoleName: studyRoleName,
                },
                studyUserType,
                studyRoleId
            );
        }


        return onCancel();
    };

    const setDataPendingSignature = () => {
        if (!data || data.length === 0) return;
        const { currentDOA } = props;
        const PIUsers = currentDOA
            .filter((item) => lodash.get(item, 'role.name', '') === PI_ROLE)
            .map((item) => item.user);
        const pendingSignaturesWithInitiated = data.pendingSignatures.length ? data.pendingSignatures : data.initiatedSignatures;
        const signatures = pendingSignaturesWithInitiated.reduce((total, cur) => {
            if (cur.signatureType.includes('PI_START') && !PIUsers.length) {
                return [...total];
            }
            if (cur.signatureType.includes('STAFF_START') && props.doaMode === 'CENTRAL') {
                return [...total];
            }

            let piUser = PIUsers.filter((item) => item.id === cur.signBy)[0];
            let [label, role, userId] = ['', '', ''];

            if (cur.signatureType.includes('PI_START') && !lodash.isUndefined(piUser)) {
                [label, role, userId] = [piUser.displayName, PI_LABEL, piUser.id];
            } else {
                [label, role, userId] = [data.delegationName, STAFF_LABEL, data.userId];
            }

            return [
                ...total,
                {
                    label,
                    chipLabel: `${label} — ${role}`,
                    text: `${label} (${role})`,
                    value: `${userId}::${role}`,
                    data: { userId, role },
                },
            ];
        }, []);
        setSelectedSignatures(signatures);

        if (hasRequiredFields()) {
            const piSignatureSuggested = collectPISignatures();
            const staffSignatureSuggested = collectStaffSignatures();
            const allSignatureSuggested = [...piSignatureSuggested, ...staffSignatureSuggested];
            const userIDsInDropdown = shouldUserInDropDownSignature(
                allSignatureSuggested,
                data.pendingSignatures
            );

            const signatureSuggested = allSignatureSuggested.filter((item) =>
                lodash.isEmpty(data.pendingSignatures)
                    ? true
                    : userIDsInDropdown.includes(item.value)
            );


            setSuggestedSignatures(signatureSuggested);
        }
    };

    const collectExistingDelegations = () => {
        const { currentDOA } = props;

        const filterStaffNotFullFields = lodash.filter(
            currentDOA,
            ({ role, startDate, user, tasks }) =>
                !(
                    lodash.isEmpty(role) ||
                    lodash.isEmpty(tasks) ||
                    lodash.isEmpty(user) ||
                    lodash.isNil(startDate)
                )
        );

        const staffDelegationsOverlappingWithNewPITenure = lodash.filter(
            filterStaffNotFullFields,
            ({ role, startDate, endDate, user }) =>
                role &&
                role.name !== PI_ROLE &&
                user.id !== data.userId &&
                isPITenureOverlappingWithDelegation(
                    [startDateSelected, endDateSelected],
                    [startDate, endDate]
                )
        );

        const delegationOptions = lodash.map(
            staffDelegationsOverlappingWithNewPITenure,
            (delegation) => ({
                id: lodash.get(delegation, 'id'),
                checked: true,
                label: lodash.get(delegation, 'user.displayName'),
                data: delegation,
            })
        );

        setDelegationRequiringNewPiSignature(delegationOptions);
    };

    const studyTeamMembersNotDelegates = useMemo(() => {
        const { staffOptions } = props;
        const allStudyTeam = get(studySettings, 'allStudyTeam', null);
        const studyId = get(props, 'study.id', null);

        if (!studySettings || !studyId) {
            return staffOptions.map(staff => ({
                ...staff,
                id: staff.value,
                displayName: staff.label
            }));

        }
        if (!lodash.isNull(allStudyTeam) && !lodash.isEmpty(allStudyTeam)) {
            return allStudyTeam.filter(team => !team.delegation && !team.studyRole);
        }
        return [];
    }, [studySettings]);

    const notStudyTeamMembersNotDelegates = useMemo(() => {
        if (!studySettings) return [];
        const { allTeamMembers, studyTeam } = studySettings;
        if (!lodash.isEmpty(studyTeam) && !lodash.isEmpty(allTeamMembers)) {
            return allTeamMembers.filter(allMembers => !studyTeam.some(member => member.userId === allMembers.userId));
        }

        return allTeamMembers;
    }, [studySettings]);

    function handleUserSelected(selectedUser, event) {
        if (!selectedUser.studyUserType) {
            setIsUserNotStudyMember(true);
        } else {
            setIsUserNotStudyMember(false);
        }
        setSelectedUser(selectedUser);
    }

    function handleDelete() {
        // if study is empty it means the drawer is been called from binder
        if (!props.study.id || adminCount <= 1) {
            handleConfirmDelete();
        } else {
            setIsDeleting(true);
        }
        setIsEditing(false);
    }

    function handleConfirmDelete() {
        const {
            removeStudyTeamMember,
            deleteStudyDelegationV2
        } = props;

        const { userId, id } = data;
        const binderId = props.currentBinder.id;
        const studyId = props.study.id;


        if (shouldRemoveUserFromStudy) {
            const [selectedUser] = props.studyTeam.filter(member => member.userId === userId) || null;
            removeStudyTeamMember(selectedUser, false, true, false);
        }
        deleteStudyDelegationV2(id, binderId, studyId);
        handleOnClose();
    }

    function getAccessLevel() {
        const accessLevels = [
            { label: 'Admin', value: 'admin' },
            { label: 'Team Member', value: 'team' },
            { label: 'Viewer', value: 'viewer' },
        ];
        if (!props.canManageStudyAdmins) {
            return accessLevels.filter(level => level.value !== 'admin');
        }
        return accessLevels;

    }


    useEffect(() => {
        if (isDeleting) {
            setHeaderLabel(`Removing ${data.delegationName} from the Delegation Log`);
        } else if (isEditing) {
            setHeaderLabel(`${data.delegationName}`);
        } else {
            setHeaderLabel('Add Delegation');
        }
    }, [isDeleting, isEditing, data]);

    const validTenureOverlaping = () => {
        const { currentDOA } = props;
        const errors = [];
        verifyNoPITenuresOverlapped(
            currentDOA,
            lodash.get(selectedRole, 'label'),
            [startDateSelected, endDateSelected],
            () => errors.push('StartDate', 'EndDate')
        );

        if (lodash.isEmpty(errors)) {
            setErrors({
                ...errors,
                tenureOverlaps: false,
            });
            return true;
        }

        setErrors({
            ...errors,
            tenureOverlaps: true,
        });
        return false;
    };

    function isRequiredCompleted() {
        if (isUserNotStudyMember && isStudyDashboard) {
            setErrors({
                user: !selectedUser,
                dataSource: !selectedDataSource,
                studyAccess: !selectedStudyAccess.value,
                role: !selectedRole,
            });
            return (
                !!selectedUser &&
                !!selectedDataSource &&
                !!selectedStudyAccess.value &&
                !!selectedRole
            );
        }

        if (!userHasDataSource) {
            setErrors({
                user: !selectedUser,
                dataSource: !selectedDataSource,
                role: !selectedRole,
            });
            return (
                !!selectedUser &&
                !!selectedDataSource &&
                !!selectedRole
            );
        }

        if (!userHasDataSource) {
            setErrors({
                user: !selectedUser,
                dataSource: !selectedDataSource,

            });
            return (
                !!selectedUser &&
                !!selectedDataSource
            );
        }
        setErrors({
            user: !selectedUser,
            role: !selectedRole,
        });
        return !!selectedUser && !!selectedRole;
    }

    async function handleAddUserToStudy() {
        const { displayName, userId } = selectedUser;
        const teamMember = {
            displayName,
            userId,
            nameOfResearcher: selectedDataSource,
            studyRoleId: selectedRole.role.id,
        };

        await props.createTeamMember(teamMember, selectedStudyAccess.value, null, false, false, false);
    }

    const onSubmit = async () => {
        if (!isRequiredCompleted() || !validTenureOverlaping() || isRequesting) return;

        setIsRequesting(true);
        const binderId = props.currentBinder.id;
        const { createStudyDelegationForStudy } = props;
        const institutionId = lodash.get(studySettings, ['staffDescriptor', 'institutionId'], null);
        const studyId = lodash.get(props, ['study', 'id'], null);
        const signatureExpectations = [];
        const newDelegationId = uuid.v4();
        let userId = get(selectedUser, 'userId');
        if (!selectedUser.id) {
            await handleAddUserToStudy();
        }

        if (!selectedUser.userId) {
            userId = get(selectedUser, 'id');
        }

        if (!userHasDataSource) {
            const teamMember = {
                ...selectedUser,
                nameOfResearcher: selectedDataSource,
            };
            await props.editStudyTeamMember(teamMember, selectedUser.studyUserType);
        }

        const delegation = cloneDeep({
            binderId,
            id: newDelegationId,
            user: {
                id: userId,
                name: get(selectedUser, 'displayName'),
            },
            roleManagementId: get(selectedRole, 'role.id', null),
            tasks: map(selectedTask, (option) => get(option, 'task')),
            startDate: moment(startDateSelected).isValid() ? moment(startDateSelected).toISOString() : null,
            endDate: moment(endDateSelected).isValid() ? moment(endDateSelected).toISOString() : null,
            notes: auditNotes,
            isRevokedPIRole: true,
            isStillStaff: true,
            auditOrigin: 'Delegation',
        });

        if (!isEmpty(selectedSignatures)) {
            const expectations = map(selectedSignatures, (option) => ({
                delegationId: newDelegationId,
                user: {
                    id: get(option, 'data.userId'),
                    name: get(option, 'label'),
                },
                signatureType: get(option, 'data.role') === PI_LABEL ? 'PI_START' : 'STAFF_START',
            }));

            signatureExpectations.push(...expectations);
        }

        if (!isEmpty(delegationRequiringNewPiSignature)) {
            const signaturesForExistingDelegation = lodash
                .chain(delegationRequiringNewPiSignature)
                .filter(({ checked }) => checked)
                .map((option) => ({
                    delegationId: lodash.get(option, 'data.id'),
                    user: {
                        id: userId,
                        name: get(selectedUser, 'displayName'),
                    },
                    signatureType: 'PI_START',
                }))
                .value();
            signatureExpectations.push(...signaturesForExistingDelegation);
        }

        const defaultSignatureExpectations = map(suggestedSignatures, (option) => ({
            delegationId: newDelegationId,
            user: {
                id: get(option, 'data.userId'),
                name: get(option, 'label'),
            },
            signatureType: get(option, 'data.role') === PI_LABEL ? 'PI' : 'STAFF',
        }));
        createStudyDelegationForStudy(institutionId, binderId, delegation, signatureExpectations, defaultSignatureExpectations, () => handleOnClose(), studyId);
    };

    function getEditFormErrors() {
        const _errors = [];
        if (lodash.get(selectedRole, 'label') === PI_ROLE) {
            const { currentDOA } = props;
            const allOverlappingPIs = lodash.filter(
                currentDOA,
                ({ role, startDate, endDate, user }) =>
                    lodash.get(role, 'name', '') === PI_ROLE &&
                    user.id !== data.userId &&
                    isPITenureOverlappingWithDelegation(
                        [startDate, endDate],
                        [startDateSelected, endDateSelected]
                    )
            );

            if (!lodash.isEmpty(allOverlappingPIs)) {
                setErrors({
                    ...errors,
                    tenureOverlaps: true
                });
                _errors.push('StartDate', 'EndDate');
            }
        }

        const hasCompletedSignatures = data.completeSignatures.length > 0;

        if (!auditNotes && hasCompletedSignatures) {
            setIsAuditNotesError(true);
            _errors.push('AuditNotes');
        }
        return !lodash.isEmpty(_errors);
    }

    const onEditDelegation = () => {
        if (getEditFormErrors() || isRequesting) return;

        setIsRequesting(true);
        const signatureExpectations = [];
        const { updateStudyDelegationV2, study } = props;
        const binderId = props.currentBinder.id;

        const isStillStaff =
            lodash.get(data, 'delegationRole', '') !== PI_ROLE &&
            lodash.get(selectedRole, 'value', '') !== PI_ROLE;
        const isChangePIToStaff =
            lodash.get(data, 'delegationRole', '') === PI_ROLE &&
            lodash.get(selectedRole, 'value', '') !== PI_ROLE;

        const delegation = lodash.cloneDeep({
            binderId,
            id: data.id,
            studyId: study.id,
            user: {
                id: data.userId,
                name: data.delegationName,
            },
            roleManagementId: lodash.get(selectedRole, 'role.id', null),
            tasks: lodash.map(selectedTask, (option) => lodash.get(option, 'task')),
            startDate: moment(startDateSelected).isValid()
                ? moment(startDateSelected).toISOString()
                : null,
            endDate: moment(endDateSelected).isValid()
                ? moment(endDateSelected).toISOString()
                : null,
            notes: auditNotes,
            isRevokedPIRole: isTheSameOldData() || !isChangePIToStaff,
            isStillStaff,
            auditOrigin: 'Delegation',
        });

        if (!lodash.isEmpty(selectedSignatures)) {
            const expectations = lodash.map(selectedSignatures, (option) => ({
                delegationId: delegation.id,
                user: {
                    id: lodash.get(option, 'data.userId'),
                    name: lodash.get(option, 'label'),
                },
                signatureType:
                    lodash.get(option, 'data.role') === PI_LABEL ? 'PI_START' : 'STAFF_START',
            }));

            signatureExpectations.push(...expectations);
        }

        if (!lodash.isEmpty(delegationRequiringNewPiSignature)) {
            const signaturesForExistingDelegation = lodash
                .chain(delegationRequiringNewPiSignature)
                .filter(({ checked }) => checked)
                .map((option) => ({
                    delegationId: lodash.get(option, 'data.id'),
                    user: {
                        id: data.userId,
                        name: data.delegationName,
                    },
                    signatureType: 'PI_START',
                }))
                .value();
            signatureExpectations.push(...signaturesForExistingDelegation);
        }

        const defaultSignatureExpectations = !isDirtyData
            ? []
            : lodash.map(suggestedSignatures, (option) => ({
                delegationId: delegation.id,
                user: {
                    id: lodash.get(option, 'data.userId'),
                    name: lodash.get(option, 'label'),
                },
                signatureType: lodash.get(option, 'data.role') === PI_LABEL ? 'PI' : 'STAFF',
            }));

        updateStudyDelegationV2(
            binderId,
            delegation,
            signatureExpectations,
            defaultSignatureExpectations,
            doCloseAndCleanup,
            study.id
        );
    };

    const parseDateFormat = (date) => {
        if (lodash.isNil(date)) {
            return null;
        }
        const dt = moment(date).format('LL');
        return moment.utc(dt);
    };

    const collectPISignatures = () => {
        if (lodash.get(selectedRole, 'label') === PI_ROLE) {
            let userId;
            let displayName;
            if (selectedUser) {
                userId = Object.prototype.hasOwnProperty.call(selectedUser, 'userId') ? selectedUser.userId : selectedUser.id;
                displayName = selectedUser.displayName;
            } else if (data) {
                userId = data.userId;
                displayName = data.delegationName;
            }
            return [
                {
                    label: displayName,
                    chipLabel: `${displayName} — ${PI_LABEL}`,
                    text: `${displayName} (${PI_LABEL})`,
                    value: `${userId}::${PI_LABEL}`,
                    data: { userId: userId, role: PI_LABEL },
                },
            ];
        }

        const { currentDOA } = props;
        return lodash
            .chain(currentDOA)
            .filter(
                ({ role, startDate, endDate }) =>
                    lodash.get(role, 'name') === PI_ROLE &&
                    isActivePI([startDate, endDate], parseDateFormat(moment()))
            )
            .map(({ user }) => {
                const { displayName, userId = null, id = null } = user;
                return {
                    label: `${displayName}`,
                    chipLabel: `${displayName} — ${PI_LABEL}`,
                    text: `${displayName} (${PI_LABEL})`,
                    value: `${userId || id}::${PI_LABEL}`,
                    data: {
                        role: PI_LABEL,
                        userId: userId || id,
                    },
                };
            })
            .uniqBy('value')
            .value();
    };

    const collectStaffSignatures = () => {
        const { doaMode } = props;
        let userId;
        let displayName;
        if (doaMode === 'CENTRAL' || lodash.get(selectedRole, 'label') === PI_ROLE) {
            return [];
        }

        if (selectedUser) {
            userId = Object.prototype.hasOwnProperty.call(selectedUser, 'userId') ? selectedUser.userId : selectedUser.id;
            displayName = selectedUser.displayName;
        } else {
            userId = data.userId;
            displayName = data.delegationName;
        }


        return [
            {
                chipLabel: `${displayName} — Staff`,
                text: `${displayName} (Staff)`,
                label: `${displayName}`,
                value: `${userId}::Staff`,
                data: { userId: userId, role: 'Staff' },
            },
        ];
    };

    const suggestSignatures = () => {
        if (lodash.isNil(selectedRole) || !isDirtyData) return;
        const signatures = [];
        const piSignatureSuggested = collectPISignatures();
        const staffSignatureSuggested = collectStaffSignatures();

        signatures.push(...piSignatureSuggested);
        signatures.push(...staffSignatureSuggested);
        setSelectedSignatures(signatures);
        setSuggestedSignatures(signatures);
    };

    const clearSuggestedSignatures = () => {
        setSuggestedSignatures([]);
        setSelectedSignatures([]);
    };

    const onSignatureChange = (value) => {
        setSelectedSignatures(value);
    };

    function handleOnClose() {
        onCancel();
    }

    const isDisableEdit = () => !props.canManageDoaLogs;

    function footerComponent() {
        if (isDisableEdit()) {
            return (
                <Footer
                    isCreating={false}
                    isEditing={true}
                    isDeleting={false}
                    setIsDeleting={() => {}}
                    setIsEditing={() => {}}
                    onCancel={handleOnClose}
                    hideDeleteButton={true}
                    disableSave={true}
                    onSaveEdit={() => {}}
                    onDelete={() => {}}
                    handleConfirmDelete={() => {}}
                    onSubmit={() => {}}
                />
            );
        }
        return (
            <Footer
                isCreating={!isEditing && !isDeleting}
                onDelete={lodash.debounce(handleDelete, 500, { leading: true })}
                handleConfirmDelete={lodash.debounce(handleConfirmDelete, 500, { leading: true })}
                isEditing={isEditing}
                isDeleting={isDeleting}
                setIsDeleting={setIsDeleting}
                setIsEditing={setIsEditing}
                onSaveEdit={lodash.debounce(onEditDelegation, 500, { leading: true })}
                onCancel={handleOnClose}
                hideDeleteButton={!props.canManageDoaLogs || isCompleteSignatures}
                onSubmit={lodash.debounce(onSubmit, 500, { leading: true })}
            />
        );
    }

    function onRemoveUserFromStudy(value) {
        setShouldRemoveUserFromStudy(value);
    }

    function onRoleSelected(value) {
        setSelectedRole(value);
        !isDirtyData && setIsDirtyData(true);
    }

    const countAdminUsers = (data) => {
        return data.filter(user => user.studyUserType === 'admin').length;
    };

    function onTaskSelected(value) {
        const sortListSelected = lodash.sortBy(value, 'value');
        const sortTasksSelected = lodash.sortBy(selectedTask, 'value');

        if (!lodash.isEqual(sortListSelected, sortTasksSelected)) {
            setSelectedTask(value);
            !isDirtyData && setIsDirtyData(true);
        }

    }

    function getDataSourceMatches() {
        const [match] = dataSourceValues.filter(val => val.value === selectedUser.displayName);
        if (!match || lodash.isEmpty(match)) {
            return handleSelectedDataSource('');
        }
        handleSelectedDataSource(match.value);
    }

    const handleCreateDataSource = (addNewValue, callbackFn) => {
        if (!addNewValue.trim()) return;

        const value = determineValueForDataType(addNewValue);
        if (!validateDuplicateValues(value)) return;

        const dataSourceItem = {
            value,
            text: getStringValue(dataType, addNewValue),
            status: dataSourceStatus.active.value,
            order: dataSourceValues.length + 1,
            dataType,
        };
        const newDataSourceValues = [...dataSourceValues, dataSourceItem];
        handleSaveDataSource(dataSourceLogActions.created, dataSourceItem, newDataSourceValues, callbackFn);
    };

    const determineValueForDataType = (value) => {
        const rawTypesNotAllowed = ['DATEONLY', 'DATETIME'];
        if (rawTypesNotAllowed.includes(dataType)) {
            return getStringValue(dataType, value);
        }
        return getRawValue(dataType, value);
    };

    const validateDuplicateValues = (value) => {
        if (some(dataSourceValues, { value })) {
            setErrors({
                ...errors,
                duplicatedDataSource: true,
            });
            return false;
        }
        return true;
    };


    const handleSaveDataSource = (action, dataSourceItem, dataSourceValues, callbackFn) => {
        const { id: dataSourceMetadataId, institutionId, name, type } = props.dataSource;
        const showToaster = false;
        const dataSourceLog = {
            ...dataSourceItem,
            value: String(dataSourceItem.value),
            action,
            dataSourceMetadataId,
            institutionId,
            name,
            description: name,
            type,
        };

        props.loadDataSourceLog(dataSourceLog);
        props.writeDataSourceValues(dataSourceValues);
        props.saveDataSource(institutionId, null, showToaster);
        handleSelectedDataSource(dataSourceItem.value);
        callbackFn();
    };

    const handleSelectedStudyAccess = (value) => {
        setSelectedStudyAccess(value);
    };

    const handleSelectedDataSource = (item) => {
        setSelectedDataSource(item);
    };

    const onStarSelected = (value) => {
        setStartDateSelected(parseDateFormat(value));

        setChangeStartDate(true);
        setCurrentEndDateChange(value);
        !isDirtyData && setIsDirtyData(true);
    };
    const onEndDateSelected = (value) => {
        setEndDateSelected(parseDateFormat(value));

        setChangeEndDate(true);
        setCurrentStartDateChanged(value);
        !isDirtyData && setIsDirtyData(true);
    };
    const handleCurrenntDateChange = (value) => {
        setCurrentStartDateChanged(value);
        setCurrentEndDateChange(value);
    };
    const handleCurrentEndDateChange = (value) => {
        setCurrentEndDateChange(value);
    };

    const onToggleNewPiSignatureForExistingDelegation = (targetDelegationId) => {
        const targetDelegation = lodash.find(
            delegationRequiringNewPiSignature,
            ({ id }) => targetDelegationId === id
        );
        targetDelegation.checked = !targetDelegation.checked;
        setDelegationRequiringNewPiSignature(lodash.cloneDeep(delegationRequiringNewPiSignature));
    };

    const getUserHasAdminRole = () => {
        if (!isEditing || lodash.isEmpty(studyTeam)) {
            return false;
        }

        const [currentUser] = studyTeam.filter(team => team.user.id === data.userId);

        if (lodash.isEmpty(currentUser) || lodash.isNil(currentUser)) {
            return false;
        }

        return currentUser.hasAdminRole;
    };

    const delegationProps = {
        data,
        startDateSelected,
        endDateSelected,
        selectedRole,
        selectedTask,
        onStarSelected,
        onEndDateSelected,
        onCurrentStartDateChange: handleCurrenntDateChange,
        onCurrentEndDateChange: handleCurrentEndDateChange,
        handleCurrentEndDateChange,
        onRoleSelected,
        onTaskSelected,
        onAuditNoteChange: (value) => setAuditNotes(value),
        onSignatureChange,
        handleCreateDataSource,
        handleSelectedDataSource,
        currentStartDate,
        currentEndDate,
        selectedUser,
        roleOptions,
        taskOptions,
        isEditing,
        suggestedSignatures,
        selectedSignatures,
        hasDataSource: userHasDataSource,
        canAddDataSource: props.canAddDataSource,
        dataSources: dataSourceValues,
        errors,
        delegationRequiringNewPISignature: delegationRequiringNewPiSignature,
        onToggleNewPiSignatureForExistingDelegation,
        isAuditNotesError: isAuditNotesError,
        binderId: props.currentBinder.id,
        study: props.study,
        institutionId: props.institutionId,
        numberOfSignatures: lodash.get(suggestedSignatures, 'length'),
        hasRequiredFields: hasRequiredFields(),
        isTheSameOldData: isTheSameOldData(),
        isDirtyData,
        hasChangeEndDate,
        hasAdminRole: getUserHasAdminRole(),
        selectedDataSource,
        isDisableEdit: isDisableEdit()
    };

    return (
        <Drawer
            anchor='right'
            open={open}
            handleOnClose={handleOnClose}
            onCancel={onCancel}
            headerTitle={headerLabel}
            renderFooter={footerComponent}
        >
            {
                isDeleting ? (
                    <Fragment>
                        <DeleteDelegationModal handleRemoveUserFromStudy={onRemoveUserFromStudy} data={data} />
                    </Fragment>
                )

                    :
                    (
                        <Fragment>

                            <div className={classes.body}>
                                <Box display={`${isEditing ? 'none' : 'block'}`} className={classes.userSection}>
                                    <UserSection
                                        studyTeamMembersNotDelegates={studyTeamMembersNotDelegates}
                                        isUserNotStudyMember={isUserNotStudyMember}
                                        notStudyTeamMembersNotDelegates={notStudyTeamMembersNotDelegates}
                                        handleUserSelected={handleUserSelected}
                                        dataSources={dataSourceValues}
                                        handleCreateDataSource={handleCreateDataSource}
                                        handleSelectedDataSource={handleSelectedDataSource}
                                        selectedStudyAccess={selectedStudyAccess}
                                        studyAccess={getAccessLevel()}
                                        handleSelectedStudyAccess={handleSelectedStudyAccess}
                                        useGroupedSelect={isStudyDashboard}
                                        selectedUser={selectedUser ? { ...selectedUser, label: selectedUser.displayName } : ''}
                                        canAddDataSource={props.canAddDataSource}
                                        errors={errors}
                                        selectedDataSource={selectedDataSource}
                                        canManageUsers={props.canManageStudyUsers}
                                        complion4FlagEnabled={complion4FlagEnabled}
                                    />
                                </Box>
                                <Box className={classes.delegationSection}>
                                    <DelegationSection {...delegationProps} />
                                </Box>
                            </div>

                        </Fragment>
                    )
            }
        </Drawer>
    );
}

export default connect(
    (state, _props) => {
        const institutionId = lodash.get(state, 'binder.layout.institutionId');

        const canAddDataSource = lodash.get(state, `me.institutionPermissions.${institutionId}.dataSources`, false);
        const dataSourceId = lodash.get(state, 'studySettings.staffDescriptor.dataSourceId', null);
        const studyId = lodash.get(state, 'study.id', null);
        const canManageDoALog = manageDoALogSelector(state);
        const canManageStudyDoA = manageStudyDelegationPredicate(state);
        return {
            studySettings: state.studySettings,
            studyTasks: state.doaStudyTasks,
            staffOptions: getDelegationAvailableUserOptions(state),
            currentDOA: delegationsList(state),
            doaMode: lodash.get(state.me, 'isInstitutionCentralized') === 'true' ? 'CENTRAL' : 'STANDARD',
            dataSource: dataSourceSelector(state),
            dataType: dataSourceDataTypeSelector(state),
            taskOptions: getDelegationTaskOptions(state),
            roleOptions: getDelegationRoleOptions(state),
            currentBinder: state.binder,
            study: lodash.get(state, 'study', {}),
            canAddDataSource,
            dataSourceId,
            canManageStudyUsers: manageStudyTeamMemberPredicate(state),
            canManageStudyAdmins: manageAdminsPredicate(state),
            canManageDoaLogs: studyId ? canManageStudyDoA : canManageDoALog,
            complion4FlagEnabled: loadHomePageV4Access(state),
            institutionId
        };
    },
    {
        removeStudyTeamMember,
        deleteStudyDelegationV2,
        saveDataSource,
        writeDataSourceValues,
        loadDataSourceLog,
        loadDataSource,
        unloadDataSource,
        createTeamMember,
        createStudyDelegationForStudy,
        editStudyTeamMember,
        updateStudyDelegationV2,
        retrieveStudyAuditLogData,
        getStudyDescriptors,
        getStudyRoles,
        getStudyTeam
    }
)(DelegationDrawer);